/**
 * #31 — Détection d'objections dans les transcripts d'appels.
 *
 * CE QUE C'EST, ET CE QUE CE N'EST PAS.
 * Le client demande une détection automatique des objections et leur
 * catégorisation. Il n'existe aujourd'hui AUCUN endpoint d'analyse côté
 * serveur : ni détection, ni stockage. Cette bibliothèque fait donc une
 * détection **par motifs, côté navigateur**, sur le transcript déjà présent.
 *
 * C'est une détection réelle — elle lit le texte de l'appel et en tire une
 * catégorie, un horodatage, la réponse de l'agent et une issue. Ce n'est PAS
 * un modèle de langage : elle ne comprend pas une formulation qu'on ne lui a
 * pas apprise. L'écran le dit en clair, et c'est ce qui devra migrer côté
 * serveur le jour où l'analyse existera.
 *
 * Les six familles sont celles nommées par le client :
 * Authority, Timing, Relevance, Too busy, Current vendor, Other.
 */

import i18n from 'i18next';

export type FamilleObjection =
  | 'authority'
  | 'timing'
  | 'relevance'
  | 'too_busy'
  | 'current_vendor'
  | 'other';

export const FAMILLES: { cle: FamilleObjection; libelleKey: string; couleur: string }[] = [
  { cle: 'authority',      libelleKey: 'pages:objections.family.authority',     couleur: '#6366f1' },
  { cle: 'timing',         libelleKey: 'pages:objections.family.timing',        couleur: '#f59e0b' },
  { cle: 'relevance',      libelleKey: 'pages:objections.family.relevance',     couleur: '#8b5cf6' },
  { cle: 'too_busy',       libelleKey: 'pages:objections.family.tooBusy',       couleur: '#06b6d4' },
  { cle: 'current_vendor', libelleKey: 'pages:objections.family.currentVendor', couleur: '#ef4444' },
  { cle: 'other',          libelleKey: 'pages:objections.family.other',         couleur: '#64748b' },
];

export const LIBELLE_FAMILLE = (c: string) => {
  const famille = FAMILLES.find((f) => f.cle === c);
  return famille ? i18n.t(famille.libelleKey) : c;
};

/** Motifs par famille. Ordre significatif : la première famille qui matche gagne. */
const MOTIFS: { famille: FamilleObjection; motifs: RegExp[] }[] = [
  {
    famille: 'authority',
    motifs: [
      /pas\s+d[ée]cisionnaire/i,
      /voir\s+avec\s+(mon|ma|le|la)\s+(directeur|directrice|responsable|patron|d[ée]partement|dg)/i,
      /ce\s+n'?est\s+pas\s+moi\s+qui\s+d[ée]cide/i,
      /il\s+faut\s+(en\s+parler|voir)\s+avec/i,
    ],
  },
  {
    famille: 'current_vendor',
    motifs: [
      /on\s+travaille\s+d[ée]j[àa]\s+avec/i,
      /nous\s+avons\s+d[ée]j[àa]\s+(un|une|notre)/i,
      /on\s+est\s+d[ée]j[àa]\s+[ée]quip[ée]s?/i,
      /on\s+ne\s+change\s+pas/i,
    ],
  },
  {
    famille: 'too_busy',
    motifs: [
      /pas\s+le\s+temps/i,
      /je\s+suis\s+en\s+r[ée]union/i,
      /je\s+suis\s+occup[ée]/i,
      /l[àa]\s+tout\s+de\s+suite\s+je\s+ne\s+peux\s+pas/i,
    ],
  },
  {
    famille: 'timing',
    motifs: [
      /ce\s+n'?est\s+pas\s+le\s+moment/i,
      /rappelez[- ]moi\s+dans/i,
      /recontactez[- ]moi\s+(dans|en)/i,
      /plus\s+tard\s+dans\s+l'?ann[ée]e/i,
      /apr[èe]s\s+(le\s+)?comit[ée]/i,
    ],
  },
  {
    famille: 'relevance',
    motifs: [
      /ne\s+correspond\s+pas\s+[àa]\s+(notre|nos)\s+besoins?/i,
      /(ça|ca)\s+ne\s+nous\s+(concerne|int[ée]resse)\s+pas/i,
      /on\s+ne\s+fait\s+pas\s+d'?appels/i,
      /pas\s+concern[ée]s?/i,
    ],
  },
  {
    famille: 'other',
    motifs: [
      /trop\s+cher/i,
      /budget\s+(serr[ée]|limit[ée]|pas\s+pr[ée]vu)/i,
      /pas\s+int[ée]ress[ée]/i,
      /envoyez[- ]moi\s+(un|une)\s+(mail|email|documentation)/i,
    ],
  },
];

/** Marques d'acquiescement du prospect APRÈS la réponse de l'agent. */
const MARQUES_PASSAGE = [
  /d'?accord/i, /allez[- ]y/i, /je\s+vous\s+[ée]coute/i, /envoyez[- ]moi\s+(ça|ca|cela)/i,
  /on\s+regardera/i, /je\s+vous\s+mets\s+en\s+copie/i, /pourquoi\s+pas/i, /entendu/i,
];
/** Marques de refus net. */
const MARQUES_ECHEC = [
  /non\s+merci/i, /on\s+ne\s+change\s+pas/i, /je\s+ne\s+suis\s+pas\s+int[ée]ress[ée]/i,
  /ne\s+me\s+rappelez\s+pas/i, /c'?est\s+[çc]a,?\s+merci/i,
];

export interface Objection {
  callId: string;
  agent: string;
  contact: string;
  entreprise: string;
  famille: FamilleObjection;
  /** La phrase du prospect qui porte l'objection. */
  resume: string;
  /** La réponse de l'agent, si elle suit. */
  reponse: string | null;
  /** 'passee' | 'non_passee' | 'indetermine' */
  statut: 'passee' | 'non_passee' | 'indetermine';
  /** Horodatage lu dans le transcript ("[01:12]"), sinon null. */
  horodatage: string | null;
  enregistrement: string | null;
  dateAppel: string | null;
  dureeSecondes: number;
  liste: string | null;
}

interface AppelLike {
  id?: string;
  transcription?: string | null;
  agent_name?: string | null;
  agent_email?: string | null;
  prospect_name?: string | null;
  prospect_company?: string | null;
  recording_url?: string | null;
  started_at?: string | null;
  created_date?: string | null;
  duration_seconds?: number | null;
  list_name?: string | null;
}

const HORODATAGE = /\[(\d{1,2}:\d{2})\]/;

function ligneParle(l: string): 'agent' | 'prospect' | null {
  if (/^\s*(\[\d{1,2}:\d{2}\]\s*)?agent\s*:/i.test(l)) return 'agent';
  if (/^\s*(\[\d{1,2}:\d{2}\]\s*)?prospect\s*:/i.test(l)) return 'prospect';
  return null;
}

/**
 * Ramène un transcript au format « une ligne par locuteur », seul format que
 * `ligneParle` sait lire.
 *
 * 2026-09-15 — CE MODULE NE VOYAIT AUCUN VRAI APPEL. Le pipeline de l'app écrit
 * la transcription en JSON — `AIService.ts` fait
 * `transcription: JSON.stringify({ segments })` avec
 * `segments = [{ speaker, text, timestamp }]`. Les locuteurs y sont, mais comme
 * CHAMP `speaker`, pas comme préfixe « Prospect : » dans le texte. Or la
 * détection faisait `texte.split('\n')` puis exigeait ce préfixe : sur une
 * chaîne JSON d'une seule ligne, `ligneParle` renvoyait `null` partout et la
 * boucle sortait sans rien détecter.
 *
 * Consequence mesuree : un appel transcrit par l'app, contenant deux objections
 * que les motifs reconnaissent, donnait ZERO detection — tout en etant compte
 * dans « appels avec transcript », ce qui faisait BAISSER le taux affiche.
 * #31 est decrite par le client comme la fonctionnalite differenciante ; elle ne
 * pouvait fonctionner que sur du texte mis en forme a la main.
 * Cf. BUGS-OUVERTS.md (OBJ-1).
 */
function enLignes(brut: string): string[] {
  const texte = brut.trim();
  if (texte.startsWith('{') || texte.startsWith('[')) {
    try {
      const parse = JSON.parse(texte);
      const segments = Array.isArray(parse) ? parse : parse?.segments;
      if (Array.isArray(segments)) {
        return segments
          .map((s) => {
            const qui = String(s?.speaker ?? '').trim();
            const dit = String(s?.text ?? '').trim();
            if (!qui || !dit) return '';
            const h = s?.timestamp ? `[${String(s.timestamp).replace(/^\[|\]$/g, '')}] ` : '';
            return `${h}${qui} : ${dit}`;
          })
          .filter(Boolean);
      }
    } catch { /* pas du JSON exploitable : on retombe sur le decoupage texte */ }
  }
  return texte.split('\n').map((l) => l.trim()).filter(Boolean);
}

/** Détecte les objections d'UN appel. Zéro, une ou plusieurs. */
export function detecterObjections(appel: AppelLike): Objection[] {
  const texte = appel.transcription || '';
  if (!texte.trim()) return [];
  const lignes = enLignes(texte);
  const sorties: Objection[] = [];

  for (let i = 0; i < lignes.length; i++) {
    const ligne = lignes[i];
    // noUncheckedIndexedAccess : l'acces indexe rend `string | undefined`.
    if (!ligne) continue;
    if (ligneParle(ligne) !== 'prospect') continue;

    const famille = MOTIFS.find((f) => f.motifs.some((m) => m.test(ligne)))?.famille;
    if (!famille) continue;

    // La réponse de l'agent = la première ligne agent qui suit.
    const suite = lignes.slice(i + 1);
    const iReponse = suite.findIndex((l) => ligneParle(l) === 'agent');
    const reponse = iReponse >= 0 ? (suite[iReponse] ?? null) : null;

    // L'issue se lit dans la RÉACTION du prospect à cette réponse, pas dans la
    // réponse elle-même : un bel argument suivi d'un « non merci » est une
    // objection non passée.
    let statut: Objection['statut'] = 'indetermine';
    if (iReponse >= 0) {
      const apres = suite.slice(iReponse + 1).find((l) => ligneParle(l) === 'prospect');
      if (apres) {
        if (MARQUES_ECHEC.some((m) => m.test(apres))) statut = 'non_passee';
        else if (MARQUES_PASSAGE.some((m) => m.test(apres))) statut = 'passee';
      } else {
        // L'agent a répondu et le prospect n'a plus rien dit : rien n'a été
        // emporté. On ne compte pas ça comme une réussite.
        statut = 'non_passee';
      }
    } else {
      statut = 'non_passee';
    }

    const nettoyer = (l: string) => l.replace(HORODATAGE, '').replace(/^\s*(agent|prospect)\s*:\s*/i, '').trim();

    sorties.push({
      callId: appel.id || '',
      agent: appel.agent_name || appel.agent_email || '—',
      contact: appel.prospect_name || '—',
      entreprise: appel.prospect_company || '—',
      famille,
      resume: nettoyer(ligne),
      reponse: reponse ? nettoyer(reponse) : null,
      statut,
      horodatage: ligne.match(HORODATAGE)?.[1] || null,
      enregistrement: appel.recording_url || null,
      dateAppel: appel.started_at || appel.created_date || null,
      dureeSecondes: Number(appel.duration_seconds || 0),
      liste: appel.list_name || null,
    });
  }
  return sorties;
}

export interface StatsObjections {
  appelsConnectes: number;
  appelsAvecObjection: number;
  pctAppelsAvecObjection: number;
  objections: number;
  objectionsRepondues: number;
  pctRepondues: number;
  objectionsPassees: number;
  pctPassees: number;
}

/** Les quatre chiffres du bandeau haut demandé par le client. */
export function calculerStats(appels: AppelLike[], objections: Objection[]): StatsObjections {
  const connectes = appels.filter((a) => (a.transcription || '').trim().length > 0);
  const idsAvec = new Set(objections.map((o) => o.callId));
  const repondues = objections.filter((o) => !!o.reponse).length;
  const passees = objections.filter((o) => o.statut === 'passee').length;
  const pct = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0);
  return {
    appelsConnectes: connectes.length,
    appelsAvecObjection: idsAvec.size,
    pctAppelsAvecObjection: pct(idsAvec.size, connectes.length),
    objections: objections.length,
    objectionsRepondues: repondues,
    pctRepondues: pct(repondues, objections.length),
    objectionsPassees: passees,
    pctPassees: pct(passees, objections.length),
  };
}

/** Une ligne par famille, pour le graphique. */
export function parFamille(objections: Objection[]) {
  return FAMILLES.map((f) => {
    const lot = objections.filter((o) => o.famille === f.cle);
    const passees = lot.filter((o) => o.statut === 'passee').length;
    return {
      ...f,
      total: lot.length,
      passees,
      pctPassage: lot.length > 0 ? Math.round((passees / lot.length) * 100) : 0,
    };
  });
}
